import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { User, Moon, Sun, Users, Copy, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SettingsPage = () => {
  const { user, familyCode, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const copyFamilyCode = () => {
    navigator.clipboard.writeText(familyCode);
    toast.success('Codice famiglia copiato!');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="space-y-6 animate-in max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Impostazioni</h1>
        <p className="text-muted-foreground">Gestisci il tuo profilo e le preferenze</p>
      </div>

      {/* Profile Card */}
      <Card data-testid="profile-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" /> Profilo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-muted-foreground">Nome</Label>
            <p className="text-lg font-medium">{user?.name}</p>
          </div>
          <div>
            <Label className="text-muted-foreground">Email</Label>
            <p className="text-lg font-medium">{user?.email}</p>
          </div>
        </CardContent>
      </Card>

      {/* Family Card */}
      <Card data-testid="family-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Famiglia
          </CardTitle>
          <CardDescription>
            Condividi questo codice con i membri della famiglia per accedere allo stesso budget
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <Label className="text-muted-foreground text-sm">Codice Famiglia</Label>
              <p className="text-2xl font-mono font-bold tracking-widest">{familyCode}</p>
            </div>
            <Button variant="outline" onClick={copyFamilyCode} data-testid="copy-family-code-settings">
              <Copy className="w-4 h-4 mr-2" /> Copia
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Appearance Card */}
      <Card data-testid="appearance-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            Aspetto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Tema scuro</p>
              <p className="text-sm text-muted-foreground">
                {theme === 'dark' ? 'Attivo' : 'Disattivo'}
              </p>
            </div>
            <Switch 
              checked={theme === 'dark'} 
              onCheckedChange={toggleTheme}
              data-testid="theme-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Logout Card */}
      <Card className="border-destructive/50" data-testid="logout-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <LogOut className="w-5 h-5" /> Esci
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Verrai disconnesso dal tuo account. Potrai accedere nuovamente in qualsiasi momento.
          </p>
          <Button variant="destructive" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Esci dall'account
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
